import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const formatarMinutos = (segundos) => {
  const minutos = Math.ceil(segundos / 60);
  return minutos <= 1 ? '1 minuto' : `${minutos} minutos`;
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const [error, setError] = useState(location.state?.message || '');
  const [loading, setLoading] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const isFormValid = email !== '' && password !== '';

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const resultado = await login(email, password);
      if (resultado?.twoFactorRequired) {
        navigate('/login/2fa', {
          state: { twoFactorToken: resultado.twoFactorToken, expiresIn: resultado.expiresIn },
        });
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      // Tentar logar com a conta já travada por tentativas de 2FA (o lockout é por conta, então
      // nem uma senha certa emite desafio novo) — a mesma mensagem que a segunda etapa mostra,
      // com o tempo real, não a genérica.
      if (err.code === 'TWO_FACTOR_RATE_LIMITED') {
        setError(`Muitas tentativas de verificação em duas etapas. Tente novamente em ${formatarMinutos(err.retryAfterSeconds || 60)}.`);
      } else {
        setError(err.message || 'Senha ou email incorretos');
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const closeError = () => {
    setError('');
  };

  return (
    <div className="bg-[#012B87] bg-fixed min-h-screen">
      <div className="max-w-[392px] mx-auto h-[90vh] flex items-center flex-col justify-center px-4 lg:px-0">
        {error && (
          <div className="w-full bg-red-600 rounded mb-4 h-10 sm:h-12 flex items-center justify-between px-4 sm:px-4 text-white">
            <div className="flex items-center gap-4 sm:gap-5">
              <i className="bi-x-octagon-fill text-lg sm:text-xl"></i>
              <p className="text-xs sm:text-sm font-light whitespace-pre-line">{error}</p>
            </div>
            <i className="bi-x text-lg sm:text-xl cursor-pointer" onClick={closeError}></i>
          </div>
        )}

        <div className="w-full bg-gradient-to-br from-[#FBEEE8] to-[#E8F6FB] rounded px-5 py-7 sm:px-8 sm:py-9 md:px-10 md:py-11">
          <div className="flex items-center justify-between">
            <h1 className="text-lg md:text-2xl font-semibold">Olá,👋</h1>
            <div className="flex items-center gap-1 text-sm font-semibold">
              <div>
                <Link to="/register">Criar conta</Link>
              </div>
              <i className="bi-arrow-right-short text-tOrange text-xl"></i>
            </div>
          </div>
          <p className="text-xs my-4 sm:my-5 text-gray-600">Faça o login para acessar o portal</p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-slate-700 text-sm">
            <div
              className={`transition-all duration-500 border rounded flex items-center gap-3 sm:gap-4 py-2 px-4 h-[50px] ${
                email ? 'border-[#2C1944]' : 'border-gray-300'
              } ${error ? 'border-red-600' : ''}`}
            >
              <i className={`bi-envelope transition-all duration-500 text-lg sm:text-xl ${error ? 'text-red-600' : 'text-slate-400'}`}></i>
              <div className="flex-1">
                <input
                  type="text"
                  id="emailInput"
                  className="w-full bg-transparent outline-none"
                  spellCheck="false"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div
              className={`transition-all duration-500 border rounded flex items-center justify-between gap-3 sm:gap-4 py-2 px-4 h-[50px] ${
                password ? 'border-[#2C1944]' : 'border-gray-300'
              } ${error ? 'border-red-600' : ''}`}
            >
              <div className="flex items-center gap-3 sm:gap-4 flex-1">
                <i className={`bi-lock transition-all duration-500 text-lg sm:text-xl ${error ? 'text-red-600' : 'text-slate-400'}`}></i>
                <div className="flex-1">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="passwordInput"
                    className="w-full bg-transparent outline-none"
                    spellCheck="false"
                    placeholder="Senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
              <i
                onClick={() => setShowPassword(!showPassword)}
                className={`${showPassword ? 'bi-eye' : 'bi-eye-slash'} text-slate-400 text-lg sm:text-xl cursor-pointer`}
              ></i>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2"></div>
              <div className="text-sky-400 underline">
                <Link to="/forgot-password">Esqueci a senha</Link>
              </div>
            </div>

            <input
              type="submit"
              value={loading ? 'Entrando...' : 'Entrar'}
              className={`submitBtn transition-all w-full rounded py-2 cursor-pointer font-semibold mt-5 sm:mt-6 ${
                isFormValid && !loading
                  ? 'bg-tBlue hover:bg-tBlue2 text-white'
                  : 'bg-gradient-to-r from-[#EBEBEB] to-[#EBEBEB] text-slate-400 cursor-not-allowed'
              }`}
              disabled={!isFormValid || loading}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
